import { Form, Input, Spin, Button, Card } from 'antd';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getChildLastStep, getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import AddressFields from 'components/AddressFields';
import { getFirstName, getPhoneNumberValidator } from 'utils';
import PhoneNumber from 'components/PhoneNumber';
import { Add, TrashCan } from '@carbon/icons-react';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenProviderInfo({
  application,
  setApplication,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  // Form state
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState(null);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Get entity index from query parameters
  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  // Load existing providers for this application
  useEffect(() => {
    const loadProviders = async () => {
      if (application?.id && currentChild?.id) {
        try {
          const existingProviders = await familyService.getApplicationProviders(application.id);
          // Filter providers for current child
          const childProviders = existingProviders.filter((p) => p?.childId === currentChild?.id);
          setProviders(childProviders);

          // If there are existing providers, populate the form
          if (childProviders.length > 0) {
            form.setFieldsValue({ providers: childProviders });
          } else {
            // Set initial empty provider
            form.setFieldsValue({ providers: [{}] });
          }
        } catch (error) {
          console.error('Error loading providers:', error);
          // Set initial empty provider if loading fails
          form.setFieldsValue({ providers: [{}] });
        }
      }
    };

    loadProviders();
  }, [application?.id, currentChild?.id, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const _childLastStep = getChildLastStep(_newApplication?.children?.[currentChildIndex]);

        // Navigate to next step
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO,
          _newApplication,
          stepConfig,
          _childLastStep === FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO
            ? currentChildIndex === _newApplication?.children?.length - 1
              ? null
              : currentChildIndex
            : currentChildIndex,
        );

        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const { providers: formProviders } = values;

        // Process each provider
        for (const providerData of formProviders) {
          if (providerData.name && providerData.phone) {
            // Only save if required fields are filled
            const providerPayload = {
              childId: currentChild?.id,
              ...providerData,
            };

            // Check if we're updating an existing provider or creating a new one
            const existingProvider = providers.find((p) => p.id === providerData.id);

            if (existingProvider) {
              // Update existing provider
              await familyService.updateApplicationProvider(application.id, existingProvider.id, providerPayload);
            } else {
              // Create new provider
              await familyService.createApplicationProvider(application.id, providerPayload);
            }
          }
        }

        // Delete removed providers from backend
        const finalProviderIds = formProviders.map((p) => p.id).filter(Boolean); // Filter out undefined/null IDs

        // Find providers that were in the original list but not in the final form
        const providersToDelete = providers
          .filter((p) => p.childId === currentChild?.id) // Only for current child
          .filter((p) => p.id && !finalProviderIds.includes(p.id)); // Exists in backend but not in form

        // Delete removed providers
        for (const providerToDelete of providersToDelete) {
          try {
            await familyService.deleteApplicationProvider(application.id, providerToDelete.id);
          } catch (error) {
            console.error('Error deleting removed provider:', error);
          }
        }

        // Refetch application
        const _application = await familyService.getApplication(application.id);
        setApplication(_application);

        _goToNextStep(_application);
      } catch (error) {
        console.error('Error saving provider info:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild?.id, currentChildIndex, providers, readOnly, setApplication, setStep, stepConfig],
  );

  if (loading || providers === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Provider Information`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner question={`Great! Let's get ${childFirstName}'s provider information`} className="my-12" />

            <Form.List name="providers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    const isFirstProvider = name === 0;
                    const showCard = fields.length > 1;

                    const formContent = (
                      <div className="space-y-4">
                        {/* Hidden ID field */}
                        <Form.Item name={[name, 'id']} hidden>
                          <Input />
                        </Form.Item>

                        {/* Provider Name */}
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Provider name"
                          rules={[{ required: true, message: 'Please enter the provider name' }]}
                        >
                          <Input data-testid={`provider-name-${name}`} />
                        </Form.Item>

                        {/* Provider Phone */}
                        <Form.Item
                          {...restField}
                          label="Provider phone"
                          name={[name, 'phone']}
                          rules={[
                            {
                              required: true,
                              message: `Please enter ${isCompletedBySponsor ? 'their' : 'your'} phone number`,
                            },
                            getPhoneNumberValidator('Invalid phone number'),
                          ]}
                          validateFirst
                          className="mb-0"
                        >
                          <PhoneNumber data-testid={`provider-phone-${name}`} />
                        </Form.Item>

                        {/* Street Address */}
                        <AddressFields
                          formListKey="providers"
                          addressKey={[name, 'address']}
                          data-testid={`provider-address-${name}`}
                          labels={{
                            street2: 'Suite/Building/Floor (optional)',
                          }}
                        />

                        {/* Provider Email */}
                        <Form.Item
                          {...restField}
                          name={[name, 'email']}
                          label="Provider email address"
                          rules={[
                            {
                              type: 'email',
                              message: 'Email address is not valid',
                            },
                          ]}
                          className="mb-0"
                        >
                          <Input data-testid={`provider-email-${name}`} />
                        </Form.Item>
                      </div>
                    );

                    const _showRemoveButton = !isFirstProvider && !readOnly;

                    if (showCard) {
                      return (
                        <Card
                          key={key}
                          title={<div className={_showRemoveButton ? 'mt-4 md:mt-0' : ''}>Provider {name + 1}</div>}
                          className="!mb-12"
                          extra={
                            _showRemoveButton ? (
                              <Button
                                type="text"
                                danger
                                icon={<TrashCan className="w-4 h-4" />}
                                onClick={() => remove(name)}
                                data-testid={`remove-provider-${name}`}
                                className="border-red-200 bg-red-100 md:border-[transparent] md:bg-transparent"
                              >
                                Remove
                              </Button>
                            ) : null
                          }
                        >
                          {formContent}
                        </Card>
                      );
                    } else {
                      // Single provider - no card, no provider number
                      return <div key={key}>{formContent}</div>;
                    }
                  })}

                  {!readOnly && (
                    <div className="text-center">
                      <Button
                        color="primary"
                        variant="filled"
                        onClick={() => add()}
                        icon={<Add className="w-6 h-6 mb-[-2px]" />}
                        className="w-full max-w-md mt-16"
                        data-testid="add-another-provider-button"
                      >
                        Add another provider for {childFirstName}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_PROVIDER_INFO,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            disabled: submitting,
            'data-testid': 'children-provider-info-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-provider-info-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
